/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import React, { useId, FC, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import {
  Button,
  Text,
  Types,
  Heading,
  Link,
  Messages,
  InputTextField,
  RichText,
  useCheckBreakpoint,
} from '@marriott/mi-ui-library';
import { getProcessEnvs, useSession } from '@marriott/mi-headless-utils';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { sortErrorObject } from '@marriott/shared/mi-helper-utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { useStore } from '../../modules/store/memberLevelStore';
import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';
import { useAccountModelStore } from '../../modules/store/accountModelStore';
import {
  getCurrentUrlParams,
  addSubDirectoryPrefix,
  scrollToClass,
  addSubDirectoryPrefixForNonEaa,
  shouldSwapName,
  fetchReferrerRelativeUrl,
  getCustomClickTrackingPageName,
  saveReferrerToHistory,
  encryptPassword,
  addClassToBlankTargetLinks,
  getSessionDataWithFallback,
} from '../../modules/utils/helper';
import {
  getCountryAndLangCode,
  handleRedirect,
  updateUserLoginDataLayer,
  validateZipCodeForUSandCanada,
  nameFieldRegex,
  nameRegExpValidation,
  checkChinaLocale,
  chineseCharRegex,
  emailOrMemberNumberError,
  updatePublicKey,
  FORM_NAME,
  updateFormSubmitDataLayer,
} from '../../modules/utils';
import {
  forgotPwrdSubmitURL,
  emailPwrdUrl,
  activateAccountUrl,
  EMAIL_DATA_KEY,
  constants,
  userPrefilledDataURL,
  API_ERROR_KEY,
  flyoutModalKeys,
  ACTIVATE_OR_FORGOT_FORM_ID,
  LOOKUP_MEMBER_NUMBER_PATH,
  NEW_PASSWORD_FORM,
  TRACKING_CONSTANTS,
  CN_FLYOUT_STEPS,
} from '../../modules/utils/constants/constants';
import { PageContext } from '../../modules/context/PageContext';
import { fetchLinkJson, setupLinkInterception, useFlyoutNavigation } from '../../modules/utils/flyoutHelper';
import { Dropdown } from '../Dropdown';
import {
  ActivateOrForgotFormProps,
  FormData,
  FormError,
  CountryOption,
  PageLevelErrors,
} from './ActivateOrForgotForm.types';
import { FlyOutModalStyle, StyledActivateOrForgotForm } from './ActivateOrForgotForm.styles';

const initialFormData: FormData = {
  memberNum: '',
  firstName: '',
  lastName: '',
  country: {
    value: '',
    id: '',
  },
  zipcode: '',
  prefillQueryParam: '',
};

// Use named rather than default exports.
export const ActivateOrForgotForm: FC<ActivateOrForgotFormProps> = ({
  authorModelData,
  setPageError,
  updatePageErrors,
  setDisablePasswordForm,
  setDisplayTypeOneError,
  setTypeTwoErrorHeader,
  setUserDetails,
  setPhoneDetails,
  model,
  isActivateAccountFlow,
  setSuccessMsg,
  isAuthorMode,
  inlineLabel = true, // use this to change input to older version
  enablePhaseOne,
  enablePhaseThree,
  uxlFailureBannerText,
  isFlyout,
  accountEvent,
  language,
  localeBasedNameSwapConfig,
  stepCounter,
}) => {
  const { isACDLenabled, datalayer, updateDatalayer } = useSession();
  const { isForgotPasswordSuccessEaa, setIsForgotPasswordSuccessEaa } = useStore(state => state);
  const {
    modelData,
    setActiveKey,
    setSignInEmailSuccess,
    tokenExpiredFlyout,
    setTokenExpiredFlyout,
    setIsContentLoading,
    setPageErrorReset,
    setValidateToken,
    setModelKey,
    setModelData,
  } = useAccountModelStore(state => state);
  const { ACTIVATE_ACCOUNT_SUBMIT, FORGOT_PASSWORD_SUBMIT, INTERNAL, ACTIVATE_ACCOUNT_LHS_SUBMIT } = TRACKING_CONSTANTS;
  const { setCommunicationOption, setMaskedPhone, setMaskedEmail } = useAccountPersistentStore(state => state);
  const [errors, setErrors] = useState<FormError>({});
  const [errorsList, setErrorsList] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [showZipcodeField, setShowZipcodeField] = useState(false);
  const [displayTypeOneErrorInside, setDisplayTypeOneErrorInside] = useState(false);
  const [toggleAccoridon, setToggleAccordion] = useState<boolean>(false);
  const [cancelButtonLink, setCancelButtonLink] = useState<string>('');
  const [isChinaPhoneNumberForChinaLocale, setIsChinaPhoneNumberForChinaLocale] = useState<boolean>(true);
  const [isLHSDisable, setIsLHSDisable] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [publicKey, setPublicKey] = useState('');
  const isTabletViewPort = useCheckBreakpoint('viewportM');

  const pageContext = useContext(PageContext);
  const serverENV = getProcessEnvs();
  const isMobileAuthFeatureEnabled = pageContext?.isMobileAuthFeatureEnabled;
  const isEAA = pageContext?.isEAASignIn;
  const authorModel = model || authorModelData;
  // Prefer currentLocale from pageContext, fallback to language prop, then client env.
  const clientEnvVars = useClientEnvVarsStore(state => state.envVarsObject);
  const currentLocale =
    pageContext?.currentLocale || language || authorModel?.language || clientEnvVars?.['ACCEPT_LANGUAGE'];
  const { countryCode } = getCountryAndLangCode(currentLocale);
  const localeCountryCode = countryCode?.toUpperCase() || constants.USA_COUNTRY_CODE;
  const isChinaLocale = checkChinaLocale(currentLocale || '');
  const nameSwapConfig =
    pageContext?.localeBasedNameSwapConfig || localeBasedNameSwapConfig || authorModel?.localeBasedNameSwapConfig;
  const inputId = useId();
  const CustomClickTrackValue = `Activate or forgotpassword Form`;
  const router = useRouter();
  const customClickTrackingPageName = getCustomClickTrackingPageName(accountEvent?.trackingPageName, router?.pathname);
  const tokenExpired = enablePhaseOne
    ? tokenExpiredFlyout || router?.query['tokenExpired']
    : router?.query['tokenExpired'];
  const clientId = router?.query['client_id'];
  const prefillData = router?.query['prefillData'];
  const shouldPrefill =
    !isAuthorMode &&
    localeCountryCode === constants.CHINA_COUNTRY_CODE &&
    isEAA &&
    isActivateAccountFlow &&
    !!prefillData &&
    !!clientId;
  const sessionRoot = getSessionDataWithFallback(pageContext?.sessionData);
  const sessionCacheData = sessionRoot?.cacheData?.data;
  const enrollmentData = sessionCacheData?.AriesReservation?.enrollmentData;
  // If session contains passwordLessEnrollment and enrollmentData details then it is passwordlessenrollment flow from confirmation page.
  const isPasswordLessEnrollment =
    sessionCacheData?.AriesReservation?.passwordLessEnrollment === 'true' && enrollmentData?.rewardsNumber;
  const cnAccountInfoMismatchError =
    authorModel?.errorMessageCorrectInfoChina || authorModel?.memberNumberAndProfileNotMatchedErrorMessage;

  const shouldBookFlowPrefill = isPasswordLessEnrollment && !shouldPrefill;
  const enablePhaseZero =
    !isEAA &&
    !isActivateAccountFlow &&
    pageContext?.featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_ACCOUNT_FORGOT_PASSWORD_REDIRECTION];

  const isCNEaaPayloadEncrypt =
    isEAA && pageContext?.featureFlags?.[FEATURE_FLAGS.CNEXPERIENCE_CNMCOM_EAA_PAYLOAD_ENCRYPT];

  const { handleFlyoutNavigation } = useFlyoutNavigation(updatePageErrors, uxlFailureBannerText);

  const resetForm = (fieldName: string) => {
    const newErrors: any = { ...errors };

    switch (fieldName) {
      case 'memberNum':
        newErrors.memberNumberError = undefined;
        newErrors.emailOrMemberError = undefined;
        break;
      case 'firstName':
        newErrors.firstNameError = undefined;
        break;
      case 'lastName':
        newErrors.lastNameError = undefined;
        break;
      case 'zipcode':
        newErrors.zipPostalCodeError = undefined;
        break;
      default:
    }
    setErrors(newErrors);
  };

  const loadNewPwrdFormInFlyout = async () => {
    const fetchedData = await fetchLinkJson(NEW_PASSWORD_FORM);
    if (fetchedData?.data) {
      // Merge new data into existing modelData
      setModelData({
        ...modelData,
        [NEW_PASSWORD_FORM]: fetchedData,
      });
      setModelKey(NEW_PASSWORD_FORM);
      setActiveKey(flyoutModalKeys.NEW_PASSWORD_FORM);
    } else {
      // Data not found
      updatePageErrors?.({
        text: uxlFailureBannerText ?? '',
        class: 'error-sev1',
        type: 'alert',
      });
    }
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    const isProhibitedCountry = checkProhibitedCountries();

    if (!isProhibitedCountry) {
      formSubmit();
    }
  };

  const formSubmit = async () => {
    if (!validateForgotPasswordFields()) {
      setIsLoading(false);
      return;
    }
    try {
      const urlParam = getCurrentUrlParams();
      await axios
        .post(
          addSubDirectoryPrefixForNonEaa(activateAccountUrl, isEAA),
          {
            accountId: formData?.memberNum,
            firstName: formData?.firstName,
            lastName: formData?.lastName,
            country: formData?.country?.id,
            postalCode: formData?.zipcode,
            ...(shouldPrefill
              ? { queryParams: formData?.prefillQueryParam }
              : isEAA && urlParam
              ? { queryParams: urlParam }
              : ''),
            cnEaaPayloadEncrypt: isCNEaaPayloadEncrypt,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              isEAA: isEAA ?? '',
            },
          }
        )
        .then(data => {
          if (isACDLenabled) {
            updateFormSubmitDataLayer(datalayer, updateDatalayer, FORM_NAME.ACTIVATE_ONLINE_ACCOUNT, true);
          }
          setPhoneDetails({
            phoneNumber: data?.data?.phoneNumber,
            transactionId: data?.data?.transactionId,
            isChinaMobileNumber: data?.data?.chinaMobileNumber,
            maskedNumber: data?.data?.maskedNumber,
            emailAddress: !isEAA || (isEAA && !data?.data?.hideEmail) ? data?.data?.emailAddress : '',
            hideEmail: data?.data?.hideEmail,
          });
          setIsChinaPhoneNumberForChinaLocale(data?.data?.chinaMobileNumber);
          if (!isTabletViewPort) {
            const element = document.getElementsByClassName('set-password-container')?.[0];
            element?.scrollIntoView();
          }
        });
      setErrors({});
      setErrorsList([]);
      setPageError([]);
      setDisablePasswordForm(false);
      setUserDetails({
        givenName: formData?.firstName || '',
        surName: formData?.lastName || '',
        memberName: formData?.memberNum || '',
        country: formData?.country?.id || '',
        postalCode: formData?.zipcode || '',
        prefillQueryParam: formData?.prefillQueryParam || '',
        mobileEditable: formData?.mobileEditable || false,
      });
      setIsLHSDisable(true);
      setIsSubmitted(true);
      if (enablePhaseOne) {
        const { setCnFlyoutStep } = useAccountModelStore.getState();
        const isCNActivationFlyout =
          checkChinaLocale(clientEnvVars?.['ACCEPT_LANGUAGE'] || currentLocale || '') &&
          isFlyout &&
          isActivateAccountFlow;
        if (isCNActivationFlyout) {
          setCnFlyoutStep(CN_FLYOUT_STEPS.CONTACT_INFO);
        } else {
          setCnFlyoutStep(null);
        }
        setActiveKey(flyoutModalKeys.SET_PASSWORD);
      }
    } catch (error: any) {
      scrollToClass();
      if (error) {
        const errorArray: any = [];
        const errorObject: any = {};
        const errorBody = error?.response?.data?.phoenixErrorMessages;
        setDisablePasswordForm(true);
        const apiFailureErrorMessage = enablePhaseOne ? uxlFailureBannerText : pageContext?.uxlErrorMessage;
        if (isACDLenabled) {
          const activateAPIResponse =
            (error?.response?.headers?.['logineventdetails'] || '').split('|')[1] || error?.response?.data?.message;
          updateFormSubmitDataLayer(
            datalayer,
            updateDatalayer,
            FORM_NAME.ACTIVATE_ONLINE_ACCOUNT,
            false,
            activateAPIResponse
          );
        }
        if (errorBody?.errorMessages?.length) {
          errorBody.errorMessages.forEach((key: string) => {
            const authoredError =
              key === 'errorMessageCorrectInfoChina' ? cnAccountInfoMismatchError : authorModel?.[key];
            const errorString = key === API_ERROR_KEY ? apiFailureErrorMessage : authoredError;
            if (errorString) {
              errorArray.push(errorString);
              errorObject[key] = errorString;
            }
          });
          if (
            errorBody.errorMessages.includes('errorMessageCorrectInfo') ||
            errorBody.errorMessages.includes('errorMessageCorrectInfoChina') ||
            errorBody.errorMessages.includes('errorMessageResetPwrd') ||
            errorBody.errorMessages.includes('phoneAlreadyAssociatedError') ||
            errorBody.errorMessages.includes(API_ERROR_KEY)
          ) {
            setDisplayTypeOneError(true);
          } else {
            setDisplayTypeOneError(false);
            setTypeTwoErrorHeader(authorModel?.errorMessageForgotPwrd);
          }
        } else {
          errorArray.push(apiFailureErrorMessage);
          setDisplayTypeOneError(true);
        }
        setPageError(errorArray);
        setErrors(errorObject);
      }
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const updatedLink =
      isEAA && authorModel?.cancelButtonCtaPath
        ? authorModel?.cancelButtonCtaPath + getCurrentUrlParams()
        : authorModel?.cancelButtonCtaPath;
    setCancelButtonLink(updatedLink);

    if (authorModel) {
      isActivateAccountFlow && setSuccessMsg && setSuccessMsg(authorModel?.successfulActivationMessage);
      if (authorModel?.countries && authorModel?.countries.length) {
        const defCountry = localeCountryCode
          ? authorModel?.countries.find(
              (country: CountryOption) => country?.id?.toUpperCase() === localeCountryCode?.toUpperCase()
            )
          : authorModel?.countries[0];
        setFormData(prevData => ({
          ...prevData,
          country: defCountry,
        }));
        if (
          defCountry?.id?.toUpperCase() === constants.CANADA_COUNTRY_CODE ||
          defCountry?.id?.toUpperCase() === constants.USA_COUNTRY_CODE
        ) {
          setShowZipcodeField(true);
        }
      }
    }

    if (shouldPrefill) {
      handlePrefillData();
    }
    if (shouldBookFlowPrefill) {
      handleBookPrefillData();
    }
    !isEAA && !isActivateAccountFlow && saveReferrerToHistory();

    isCNEaaPayloadEncrypt && !isActivateAccountFlow && updatePublicKey(setPublicKey);
  }, []);

  const handleCountryNameChange = (option: any) => {
    if (
      option.id?.toUpperCase() === constants.CANADA_COUNTRY_CODE ||
      option.id?.toUpperCase() === constants.USA_COUNTRY_CODE
    ) {
      setShowZipcodeField(true);
    } else {
      setShowZipcodeField(false);
    }
    setFormData(prevData => ({ ...prevData, country: { ...option } }));
  };

  const checkProhibitedCountries = () => {
    if (
      serverENV?.PROHIBITED_COUNTRIES_CODES &&
      (serverENV?.PROHIBITED_COUNTRIES_CODES.includes(formData?.country?.id) ||
        serverENV?.PROHIBITED_COUNTRIES_CODES.includes(localeCountryCode))
    ) {
      const newErrors: any = {};
      newErrors.prohibitedCountryErrMsg = authorModel?.prohibitedCountryErrMsg;
      setErrors(newErrors);
      setPageError([]);
      updatePageErrors({
        text: authorModel?.prohibitedCountryErrMsg,
        class: 'error-sev1',
        type: 'alert',
      });
      setIsLoading(false);
      return true;
    } else {
      return false;
    }
  };

  const handleKeyPress = (ev: any) => {
    /**
     * handle key press event on input field that will submit the
     * form
     */
    if (ev.key === 'Enter' || ev.keyCode === 13) {
      isActivateAccountFlow ? handleSubmit(ev) : handleForgotPasswordSubmit(ev);
    }
  };

  const checkChineseCharRegex = () => {
    if (isEAA) {
      return false;
    } else if (isActivateAccountFlow) {
      return checkChinaLocale(currentLocale || '');
    } else {
      return false;
    }
  };
  const checkRegexChineseandEnglish = (value: string) => {
    if (isEAA) {
      return !checkChinaLocale(currentLocale || '') && !nameFieldRegex.test(value);
    } else {
      if (isActivateAccountFlow) {
        return checkChinaLocale(currentLocale || '')
          ? !nameFieldRegex.test(value) && !chineseCharRegex.test(value)
          : !nameFieldRegex.test(value);
      } else {
        return !nameFieldRegex.test(value);
      }
    }
  };

  const handleForgotPasswordSubmit = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    if (!validateForgotPasswordFields()) {
      setIsLoading(false);
      return;
    }
    const urlParam = getCurrentUrlParams();
    const param = new URLSearchParams(urlParam);
    const returnToQueryParam = param?.get('returnTo');
    try {
      await axios
        .post(
          addSubDirectoryPrefixForNonEaa(forgotPwrdSubmitURL, isEAA),
          {
            country: formData?.country?.id,
            givenName: formData?.firstName,
            surname: formData?.lastName,
            username:
              isCNEaaPayloadEncrypt && formData?.memberNum
                ? encryptPassword(formData?.memberNum, publicKey)
                : formData?.memberNum,
            postalCode: formData?.zipcode?.length ? formData?.zipcode : null,
            ...(enablePhaseZero
              ? { returnUrl: encodeURIComponent(returnToQueryParam || fetchReferrerRelativeUrl()) }
              : enablePhaseOne && {
                  returnUrl: encodeURIComponent(
                    window.location.pathname + window.location.search + window.location.hash
                  ),
                }),
            ...(isEAA && urlParam && { queryParams: urlParam }),
            ...(enablePhaseOne && { flyout: enablePhaseOne }),
            cnEaaPayloadEncrypt: isCNEaaPayloadEncrypt,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              isEAA: isEAA ?? '',
            },
          }
        )
        .then(async (response: any) => {
          if (response?.data?.status === 'SUCCESS') {
            if (isACDLenabled) {
              updateFormSubmitDataLayer(datalayer, updateDatalayer, FORM_NAME.FORGOT_PASSWORD, true);
            }
            setCommunicationOption?.(response?.data?.communicationOption);
            enablePhaseOne && setTokenExpiredFlyout(false);
            if (
              response?.data?.isRedirect &&
              response?.data?.resetToken &&
              response?.data?.validateToken &&
              response?.data?.phoneNumber
            ) {
              setMaskedPhone(response?.data?.phoneNumber);
              setMaskedEmail('');
              if (enablePhaseOne) {
                if (response?.data?.validateToken) {
                  setValidateToken?.(response.data.validateToken);
                }
                loadNewPwrdFormInFlyout();
              } else {
                handleRedirect(addSubDirectoryPrefixForNonEaa(response?.data?.nextStateURI, isEAA), false, false);
              }
            } else if (response?.data?.emailAddress) {
              sessionStorage.setItem(EMAIL_DATA_KEY, response?.data?.emailAddress);
              if (isEAA) {
                setIsForgotPasswordSuccessEaa(true);
                setIsLoading(false);
              } else {
                setPageErrorReset(true);
                if (response?.data?.validateToken) {
                  setValidateToken?.(response.data.validateToken);
                }
                if (response?.data?.emailAddress) {
                  setMaskedEmail?.(response.data.emailAddress);
                  setMaskedPhone('');
                }
                if (enablePhaseThree) {
                  loadNewPwrdFormInFlyout();
                  return;
                }
                setIsLoading(false);
                if (enablePhaseOne) {
                  setActiveKey(flyoutModalKeys.SIGN_IN);
                  setSignInEmailSuccess(true);
                  setPageErrorReset(true);
                } else {
                  response?.data?.resetToken && response?.data?.validateToken
                    ? handleRedirect(addSubDirectoryPrefix(response?.data?.nextStateURI), false, false)
                    : handleRedirect(addSubDirectoryPrefix(emailPwrdUrl));
                }
              }
            } else if (response?.data?.isRedirect) {
              // for mcon non -china,
              if (!isEAA && enablePhaseOne && response?.data?.nextStateURI?.includes(emailPwrdUrl)) {
                setMaskedPhone('');
                setMaskedEmail?.('');
                setCommunicationOption?.('');
                setActiveKey(flyoutModalKeys.SIGN_IN);
                setSignInEmailSuccess(true);
                setPageErrorReset(true);
              } else {
                handleRedirect(addSubDirectoryPrefixForNonEaa(response?.data?.nextStateURI, isEAA));
              }
            }
          } else {
            setDisplayTypeOneErrorInside && setDisplayTypeOneErrorInside(true);
            setErrorsList &&
              setErrorsList(
                isEAA && checkChinaLocale(currentLocale || '')
                  ? [cnAccountInfoMismatchError]
                  : [authorModel?.errorMessageCorrectInfo]
              );
            setIsLoading(false);
          }
        });
    } catch (error: any) {
      scrollToClass();
      setIsLoading(false);
      if (error) {
        const errorArray: any = [];
        const errorObject: any = {};
        const errorBody = error?.response?.data?.phoenixErrorMessages;
        const fallbackErrorMessage = enablePhaseOne ? uxlFailureBannerText : pageContext?.uxlErrorMessage;
        if (errorBody?.errorMessages[0] === 'accountInfoMismatchErrorMsg') {
          setErrorsList && setErrorsList([addClassToBlankTargetLinks(authorModel[errorBody?.errorMessages[0]])]);
        } else {
          setErrorsList && setErrorsList([authorModel[errorBody?.errorMessages[0]] ?? fallbackErrorMessage]);
        }
        if (errorBody?.errorMessages?.length) {
          setDisplayTypeOneErrorInside && setDisplayTypeOneErrorInside(true);
        } else {
          setDisplayTypeOneErrorInside(true);
        }
        // if ACDL is enabled, datalayer update don't need to be triggered here
        if (!isACDLenabled) {
          updateUserLoginDataLayer(error?.response?.headers?.['logineventdetails'] || '', false, {});
        } else {
          const forgotPwrdAPIResponse =
            (error?.response?.headers?.['logineventdetails'] || '').split('|')[1] || error?.response?.data?.message;
          updateFormSubmitDataLayer(
            datalayer,
            updateDatalayer,
            FORM_NAME.FORGOT_PASSWORD,
            false,
            forgotPwrdAPIResponse
          );
        }
        setPageError && setPageError(errorArray);
        setErrors && setErrors(errorObject);
      }
    }
  };

  const handlePrefillData = () => {
    try {
      setIsLHSDisable(true);
      axios
        .post(
          userPrefilledDataURL,
          {
            clientId,
            prefillData,
            cnEaaPayloadEncrypt: isCNEaaPayloadEncrypt,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              isEAA: isEAA ?? '',
            },
          }
        )
        .then((response: any) => {
          let hasAllFields = false;
          if (response.data?.status === 'SUCCESS') {
            const filteredCountry =
              authorModel?.countries?.find((country: any) => country.id === response?.data?.countryCode) || '';
            if (
              response.data?.countryCode?.id === constants.CANADA_COUNTRY_CODE ||
              response.data?.countryCode?.id === constants.USA_COUNTRY_CODE
            ) {
              setShowZipcodeField(true);
            }
            if (response.data) {
              setFormData(prevData => ({
                ...prevData,
                firstName: response.data?.firstName ?? '',
                lastName: response.data?.lastName ?? '',
                memberNum: response.data?.memberNumber ?? '',
                zipcode: showZipcodeField ? response.data?.postalCode : '',
                ...(filteredCountry && { country: filteredCountry }),
                prefillQueryParam: response.data?.queryParams,
                mobileEditable: response.data?.mobileEditable,
              }));
              if (
                response.data?.firstName &&
                response.data?.lastName &&
                response.data?.memberNumber &&
                filteredCountry &&
                ((showZipcodeField && response.data?.postalCode) || !showZipcodeField)
              ) {
                hasAllFields = true;
              }
            }
          }
          setIsLHSDisable(hasAllFields);
        });
    } catch (error: any) {
      setIsLHSDisable(false);
    }
  };
  const handleBookPrefillData = () => {
    try {
      setIsLHSDisable(true);
      let hasAllFields = false;
      let isShowZipcodeField = false;

      const filteredCountry =
        authorModel?.countries?.find((country: any) => country.id === enrollmentData?.country) || '';
      if (
        enrollmentData?.country?.toUpperCase() === constants.CANADA_COUNTRY_CODE ||
        enrollmentData?.country?.toUpperCase() === constants.USA_COUNTRY_CODE
      ) {
        setShowZipcodeField(true);
        isShowZipcodeField = true;
      }
      if (enrollmentData) {
        setFormData(prevData => ({
          ...prevData,
          firstName: enrollmentData?.firstName ?? '',
          lastName: enrollmentData?.lastName ?? '',
          memberNum: enrollmentData?.rewardsNumber ?? '',
          zipcode: isShowZipcodeField ? enrollmentData?.postalCode : '',
          ...(filteredCountry && { country: filteredCountry }),
        }));
        if (
          enrollmentData?.firstName &&
          enrollmentData?.lastName &&
          enrollmentData?.rewardsNumber &&
          filteredCountry &&
          ((isShowZipcodeField && enrollmentData?.postalCode) || !isShowZipcodeField)
        ) {
          hasAllFields = true;
        }
      }
      setIsLHSDisable(hasAllFields);
    } catch (error: any) {
      setIsLHSDisable(false);
    }
  };

  //sort the firstname and lastname error messages
  const getSortedErrorList = (errors: PageLevelErrors) => {
    const reverseOrder = isReverseFieldOrder();
    return reverseOrder ? Object.values(sortErrorObject(errors)) : Object.values(errors);
  };

  const validateForgotPasswordFields = () => {
    const newErrors: any = {};

    let isValid = true;
    if (!formData?.memberNum) {
      if (isActivateAccountFlow) {
        newErrors.memberNumberError = authorModel?.memberNumberError;
      } else {
        newErrors.emailOrMemberError =
          isMobileAuthFeatureEnabled || isChinaLocale
            ? authorModel?.credentialsErrorMessage
            : authorModel?.emailOrMemberError;
      }
      isValid = false;
    }
    if (!isActivateAccountFlow && formData?.memberNum && emailOrMemberNumberError(formData?.memberNum)) {
      newErrors.emailOrMemberError = authorModel?.invalidEmailOrMemberError;
      isValid = false;
    }
    if (!formData?.firstName) {
      newErrors.firstNameError = authorModel?.firstNameError;
      isValid = false;
    } else if (!!formData?.firstName && checkRegexChineseandEnglish(formData?.firstName)) {
      newErrors.firstNameInlineError = authorModel?.nameInlineErrorMsg;
      newErrors.firstNameBannerError = authorModel?.firstNameBannerError;
      isValid = false;
    }
    if (!formData?.lastName) {
      newErrors.lastNameError = authorModel?.lastNameError;
      isValid = false;
    } else if (!!formData?.lastName && checkRegexChineseandEnglish(formData?.lastName)) {
      newErrors.lastNameInlineError = authorModel?.nameInlineErrorMsg;
      newErrors.lastNameBannerError = authorModel?.lastNameBannerError;
      isValid = false;
    }
    if (showZipcodeField) {
      if (!formData?.zipcode) {
        newErrors.zipPostalCodeError = authorModel?.zipPostalCodeError;
        isValid = false;
      } else if (!validateZipCodeForUSandCanada(formData?.country?.id || '', formData?.zipcode)) {
        newErrors.zipPostalCodeError = authorModel?.zipInlineError || '';
        isValid = false;
      }
    }

    setErrors(newErrors);
    const excludeKeys = ['firstNameInlineError', 'lastNameInlineError'];

    const pageLevelErrors = Object.fromEntries(Object.entries(newErrors).filter(([key]) => !excludeKeys.includes(key)));
    setErrorsList(getSortedErrorList(pageLevelErrors as PageLevelErrors));

    if (isActivateAccountFlow) {
      setPageError(getSortedErrorList(pageLevelErrors as PageLevelErrors));

      setDisplayTypeOneError && setDisplayTypeOneError(false);
      setTypeTwoErrorHeader && setTypeTwoErrorHeader(authorModel?.errorMessageForgotPwrd);
    }
    setDisplayTypeOneErrorInside && setDisplayTypeOneErrorInside(false);
    !isValid && scrollToClass();
    return isValid;
  };

  const renderFirstNameField = () => (
    <InputTextField
      label={
        isChinaLocale && isEAA && isActivateAccountFlow
          ? authorModel?.firstNameLabelChinese
          : authorModel?.firstNameLabel
      }
      testId="first-name"
      inputValue={formData?.firstName}
      showErrorMessage={!!errors?.firstNameError || !!errors?.firstNameInlineError}
      messageToShow={errors?.firstNameError || errors?.firstNameInlineError}
      setErrorHtml={true}
      messageClass="error-label"
      inputMaxLength={20}
      infoLabel={`${
        isChinaLocale && isEAA && isActivateAccountFlow
          ? authorModel?.firstNameLabelChinese
          : authorModel?.firstNameLabel
      } - ${authorModel?.title}`}
      customDownshiftLabel={true}
      getInputProps={() => ({
        /** setReadOnly props for remeber case */
        autoComplete: 'off',
        onKeyUp: handleKeyPress,
        id: `firstname`,
      })}
      getLabelProps={() => ({
        htmlFor: `firstname`,
      })}
      disabled={isLHSDisable}
      className={clsx(
        enablePhaseOne ? 'pb-2 mt-3' : 'pb-2 mt-5',
        { 'is-disabled': isLHSDisable },
        errors?.firstNameError || errors?.firstNameInlineError ? 'is-error' : formData?.firstName ? 'is-active' : '',
        inlineLabel ? 'm-input-text-field-v3' : 'm-input-field'
      )}
      getInputValue={(val: string) => {
        val && resetForm('firstName');
        setFormData({ ...formData, firstName: val?.trim() });
        !(isEAA && checkChinaLocale(currentLocale || '')) &&
          nameRegExpValidation(
            val,
            'firstName',
            'firstNameInlineError',
            setErrors,
            errors,
            authorModel.nameInlineErrorMsg,
            checkChineseCharRegex()
          );
      }}
    />
  );

  const renderLastNameField = () => {
    return (
      <InputTextField
        label={
          isChinaLocale && isEAA && isActivateAccountFlow
            ? authorModel?.lastNameLabelChinese
            : authorModel?.lastNameLabel
        }
        testId="last-name"
        inputValue={formData?.lastName}
        showErrorMessage={!!errors?.lastNameError || !!errors?.lastNameInlineError}
        messageToShow={errors?.lastNameError || errors?.lastNameInlineError}
        setErrorHtml={true}
        messageClass="error-label"
        inputMaxLength={25}
        infoLabel={`${
          isChinaLocale && isEAA && isActivateAccountFlow
            ? authorModel?.lastNameLabelChinese
            : authorModel?.lastNameLabel
        } - ${authorModel?.title}`}
        customDownshiftLabel={true}
        getInputProps={() => ({
          /** setReadOnly props for remeber case */
          autoComplete: 'off',
          onKeyUp: handleKeyPress,
          id: `lastName`,
        })}
        getLabelProps={() => ({
          htmlFor: `lastName`,
        })}
        disabled={isLHSDisable}
        className={clsx(
          enablePhaseOne ? 'pb-2 mt-3' : 'pb-2 mt-5',
          { 'is-disabled': isLHSDisable },
          errors?.lastNameError || errors?.lastNameInlineError ? 'is-error' : formData?.lastName ? 'is-active' : '',
          inlineLabel ? 'm-input-text-field-v3' : 'm-input-field'
        )} //is-error,is-active
        getInputValue={(val: string) => {
          val && resetForm('lastName');
          setFormData({ ...formData, lastName: val?.trim() });
          !(isEAA && checkChinaLocale(currentLocale || '')) &&
            nameRegExpValidation(
              val,
              'lastName',
              'lastNameInlineError',
              setErrors,
              errors,
              authorModel.nameInlineErrorMsg,
              checkChineseCharRegex()
            );
        }}
      />
    );
  };

  const isReverseFieldOrder = () => {
    const localeSwapCheck = shouldSwapName(nameSwapConfig, localeCountryCode);
    return localeSwapCheck;
  };

  const shouldRenderCopyright =
    localeCountryCode === constants.JAPAN_COUNTRY_CODE ||
    localeCountryCode === constants.KOREA_COUNTRY_CODE ||
    localeCountryCode === constants.CHINA_COUNTRY_CODE;

  const lookUpClickHandler = async () => {
    setIsContentLoading(true);
    await handleFlyoutNavigation(LOOKUP_MEMBER_NUMBER_PATH); // Use correct href key
    setIsContentLoading(false);
  };

  useEffect(() => {
    let cleanupFn: (() => void) | undefined;
    if (enablePhaseThree) {
      const { interceptAnchorClicks } = setupLinkInterception(LOOKUP_MEMBER_NUMBER_PATH, lookUpClickHandler);
      cleanupFn = interceptAnchorClicks(ACTIVATE_OR_FORGOT_FORM_ID);
    }

    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, []);

  const renderFields = () => {
    // debugger;
    return (
      <form
        data-component-name="m-account-ActivateOrForgotForm"
        data-testid="account-ActivateOrForgotForm"
        method="post"
        id="submit"
        onSubmit={isActivateAccountFlow ? handleSubmit : handleForgotPasswordSubmit}
      >
        <div className="activate-form">
          {isActivateAccountFlow && !enablePhaseOne ? (
            <div className="activate-form__heading t-subtitle-xl mb-4">{authorModel?.subTitle}</div>
          ) : (
            ''
          )}
          {tokenExpired && (
            <Messages messageType="warning" className="account-page-error-msg mb-5 px-2">
              <RichText text={authorModel?.resetPwrdLinkExpired} componentId="reset-token-error" />
            </Messages>
          )}
          <div className="activate-form__body">
            <div className="row">
              <div className={clsx('col-12', isActivateAccountFlow && !isFlyout ? 'col-lg-11' : 'col-lg-12')}>
                <div
                  className={clsx(
                    'pb-2',
                    inlineLabel ? 'm-input-text-field' : 'm-input-field ',
                    { 'is-disabled': isLHSDisable },
                    errors?.memberNumberError || errors?.emailOrMemberError
                      ? 'is-error'
                      : formData?.memberNum
                      ? 'is-active'
                      : ''
                  )}
                >
                  <InputTextField
                    infoLabel="memnum"
                    label={
                      isActivateAccountFlow
                        ? authorModel?.memberNumberLabel
                        : isMobileAuthFeatureEnabled || isChinaLocale
                        ? authorModel?.credentialsLabel
                        : authorModel?.emailOrMemberLabel
                    }
                    inputValue={formData?.memberNum}
                    inputMaxLength={isActivateAccountFlow ? 9 : 80}
                    testId="memnum"
                    disabled={isLHSDisable}
                    getInputProps={() => ({
                      /** setReadOnly props for remeber case */
                      autoComplete: 'off',
                      id: 'memberNum',
                      type: 'text',
                      name: 'memberNum',
                    })}
                    getLabelProps={() => ({
                      htmlFor: 'memberNum',
                      [`data-testid`]: 'member-number-label',
                    })}
                    className={clsx(inlineLabel ? 'm-input-text-field-v3' : 'm-input-field', {
                      'is-disabled': isLHSDisable,
                    })}
                    getInputValue={(val: string) => {
                      val && resetForm('memberNum');
                      if (!isActivateAccountFlow) {
                        if (val && emailOrMemberNumberError(val)) {
                          setErrors({ ...errors, emailOrMemberError: authorModel?.invalidEmailOrMemberError });
                        } else {
                          setErrors({ ...errors, emailOrMemberError: '' });
                        }
                      }
                      setFormData(prevData => ({ ...prevData, memberNum: val?.trim() }));
                    }}
                    messageToShow={errors?.memberNumberError || errors?.emailOrMemberError || ''}
                    showErrorMessage={!!errors.memberNumberError || !!errors.emailOrMemberError}
                  />
                  {!isActivateAccountFlow &&
                    !(isMobileAuthFeatureEnabled || isChinaLocale) &&
                    !errors?.emailOrMemberError && (
                      <RichText
                        text={addClassToBlankTargetLinks(authorModel?.errorMessageSignIn)}
                        customClass="mt-1 t-label-xs t-font-weight-r"
                        componentId="inlinetextmsg"
                      />
                    )}
                  {isActivateAccountFlow &&
                    (!isEAA && checkChinaLocale(currentLocale) ? (
                      <div className="mt-1">
                        <RichText
                          text={authorModel?.inlineTextMessageForChina || authorModel?.inlineTextMessage}
                          customClass="t-label-xs t-font-weight-r"
                          componentId="inlinetextmsg"
                        />
                      </div>
                    ) : (shouldRenderCopyright || !isEAA) &&
                      !(formData?.country?.id === 'CN' && !isChinaPhoneNumberForChinaLocale) ? (
                      <div className="mt-1">
                        <RichText
                          text={addClassToBlankTargetLinks(authorModel?.inlineTextMessage)}
                          customClass="t-label-xs t-font-weight-r"
                          componentId="inlinetextmsg"
                        />
                      </div>
                    ) : (
                      ''
                    ))}

                  {!isActivateAccountFlow &&
                    (isMobileAuthFeatureEnabled || isChinaLocale) &&
                    !errors?.emailOrMemberError && (
                      <div
                        className={clsx(
                          'accordion accordion-container m-accordion-container ps-0 mt-1',
                          !toggleAccoridon ? 'mb-1' : 'mb-3'
                        )}
                        id="accordion-forgotpassword"
                      >
                        {/* TODO:// need confirmation on accordion atoms in mi-ui-lib */}
                        <div className="accordion-item">
                          <div className="p-0 accordion-header px-0 mb-1" id="headingOne-signin">
                            <button
                              className="accordion-button collapsed p-0 ms-0 border-0"
                              type="button"
                              id="accordian_button"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseOne-signin"
                              aria-expanded={toggleAccoridon}
                              aria-controls="collapseOne-signin"
                              onClick={() => setToggleAccordion(!toggleAccoridon)}
                            >
                              <div className="m-accordion-title d-flex align-items-center">
                                <p className="accordian-title t-label-xs mb-0">
                                  {authorModel.phoneNumberVerificationMessage}
                                </p>
                                <span
                                  className={clsx(
                                    'icon-arrow-down accordion-arrow-icon pl-2 icon-xs arrow-icon',
                                    toggleAccoridon ? 'icon-arrow-up' : 'icon-arrow-down'
                                  )}
                                ></span>
                              </div>
                            </button>
                          </div>
                          <div
                            id="collapseOne-signin"
                            className={clsx('accordion-collapse collapse', { show: toggleAccoridon })}
                            aria-labelledby="headingOne-signin"
                            data-bs-parent="#accordionExample-signin"
                          >
                            <div
                              className="accordion-body t-label-xs"
                              dangerouslySetInnerHTML={{ __html: authorModel?.membershipVerificationMessage ?? '' }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    )}
                  {!isActivateAccountFlow &&
                    (isMobileAuthFeatureEnabled || isChinaLocale) &&
                    !errors?.emailOrMemberError &&
                    (!isEAA || (isEAA && localeCountryCode === constants.CHINA_COUNTRY_CODE)) && (
                      <RichText
                        text={authorModel?.membershipPwrdResetMessage}
                        customClass="t-label-xs help-text"
                        componentId="membership-reset-message"
                      />
                    )}
                  {/* (isActivateAccountFlow && isMobileAuthFeatureEnabled && !errors?.emailOrMemberError) || */}
                  <span className="t-label-xs help-text">{authorModel?.memberNumberInfoLabel}</span>
                </div>
                {isReverseFieldOrder()
                  ? [renderLastNameField(), renderFirstNameField()]
                  : [renderFirstNameField(), renderLastNameField()]}
                <div
                  className={clsx(
                    enablePhaseOne ? 'mt-3' : 'mt-5',
                    'country-dropdown',
                    inlineLabel ? 'm-input-text-field-v3 country-dropdown__inline' : 'm-input-field',
                    { 'is-disabled': isLHSDisable },
                    { 'pb-2': showZipcodeField }
                  )}
                >
                  {!inlineLabel && (
                    <label
                      className="country-label"
                      id="dropdown-label-fp-country"
                      htmlFor="dropdown-label-fp-country"
                      data-testid="country-region-label"
                    >
                      {authorModel?.countryRegionLabel}
                    </label>
                  )}
                  <Dropdown
                    defaultOption={formData?.country?.value || ''}
                    dropdownOptions={authorModel?.countries ?? []}
                    onChange={handleCountryNameChange}
                    customClassName={clsx(
                      'activate_account--countrydropdown',
                      inlineLabel
                        ? 'activate_account--countrydropdown__inline'
                        : 'm-input-field activate_account--countrydropdown__standard'
                    )}
                    dropdownId="fp-country"
                    isDisabled={isLHSDisable}
                    hasLabel={inlineLabel ? authorModel?.countryRegionLabel : ''}
                    aria-label={`${authorModel?.countryRegionLabel} - ${authorModel?.title}`}
                  />
                </div>
                {showZipcodeField && (
                  <InputTextField
                    label={authorModel?.zipPostalCodeLabel}
                    inputValue={formData?.zipcode}
                    showErrorMessage={errors?.zipPostalCodeError !== undefined}
                    messageToShow={errors?.zipPostalCodeError}
                    setErrorHtml={true}
                    messageClass="error-label"
                    inputMaxLength={10}
                    infoLabel={`${authorModel?.zipPostalCodeLabel} - ${authorModel?.title}`}
                    customDownshiftLabel={true}
                    getInputProps={() => ({
                      /** setReadOnly props for remeber case */
                      autoComplete: 'off',
                      onKeyUp: handleKeyPress,
                      id: `${inputId}-zipcode`,
                    })}
                    getLabelProps={() => ({
                      htmlFor: `${inputId}-zipcode`,
                    })}
                    className={clsx(
                      enablePhaseOne ? 'mt-3' : 'mt-5',
                      inlineLabel ? 'm-input-text-field-v3' : 'm-input-field',
                      { 'is-disabled': isLHSDisable },
                      errors?.zipPostalCodeError ? 'is-error' : formData?.zipcode ? 'is-active' : ''
                    )} //is-error,is-active
                    getInputValue={(val: string) => {
                      val && resetForm('zipcode');
                      setFormData({ ...formData, zipcode: val?.trim() });
                    }}
                  />
                )}
              </div>
            </div>
            <div className="col-12 d-flex align-items-center flex-column flex-md-row mt-5 px-0 rtl-content">
              {isActivateAccountFlow ? (
                <Button
                  className={clsx('m-button-m m-button-primary custom-btn px-4', {
                    disabled: isLHSDisable && isSubmitted,
                  })}
                  type={Types.ButtonTypeVariation.Submit}
                  isDisabled={isLoading}
                  testId="continue-btn"
                  ariaLabel={authorModel?.continueButtonLabel}
                  custom_click_track_value={
                    isEAA
                      ? `${customClickTrackingPageName}|${ACTIVATE_ACCOUNT_LHS_SUBMIT}|${INTERNAL}`
                      : isFlyout
                      ? `${customClickTrackingPageName}|${ACTIVATE_ACCOUNT_SUBMIT}|${INTERNAL}`
                      : `${CustomClickTrackValue}|${authorModel?.submitButtonLabel}|Submit`
                  }
                >
                  <div className={clsx(!isLoading ? 'd-none' : 'm-spinner-s')} data-testid="activate-spinner"></div>
                  <div className={clsx(isLoading ? 'd-none' : '')}>{authorModel?.continueButtonLabel}</div>
                </Button>
              ) : (
                <>
                  <Button
                    className="m-button-m m-button-primary custom-btn submit_btn"
                    // callback={handleForgotPasswordSubmit}
                    type={Types.ButtonTypeVariation.Submit}
                    isDisabled={isLoading}
                    testId="forgot-btn-submit"
                    custom_click_track_value={
                      isEAA || isFlyout
                        ? `${customClickTrackingPageName}|${FORGOT_PASSWORD_SUBMIT}|${INTERNAL}`
                        : `${CustomClickTrackValue}|${authorModel?.submitButtonLabel}|Submit`
                    }
                    ariaLabel={enablePhaseThree ? authorModel?.continueButtonLabel : authorModel?.submitButtonLabel}
                  >
                    <div className={clsx(!isLoading ? 'd-none' : 'm-spinner-s')} data-testid="forgot-spinner"></div>
                    <div className={clsx(isLoading ? 'd-none' : '')}>
                      {enablePhaseThree ? authorModel?.continueButtonLabel : authorModel?.submitButtonLabel}
                    </div>
                  </Button>
                  {!enablePhaseOne && (
                    <Link
                      skipFlyout={true}
                      text={authorModel?.cancelButtonLabel}
                      linkClassName="m-link-action mt-4 mt-md-0 ml-md-5"
                      linkHref={cancelButtonLink}
                      target="_self"
                      linkType="internal"
                    />
                  )}
                </>
              )}
            </div>
            {isEAA ? (
              <Text
                customClass={`t-font-xs pt-2 mt-5 ${isActivateAccountFlow ? 'd-none d-lg-flex' : ''}`}
                copyText={authorModel?.copyright?.replace('{0}', String(new Date().getFullYear()))}
                fontSize={Types.size.extraSmall}
                element={Types.tags.paragraph}
              />
            ) : (
              ''
            )}
          </div>
        </div>
      </form>
    );
  };

  return (
    <div id={ACTIVATE_OR_FORGOT_FORM_ID}>
      {!isForgotPasswordSuccessEaa && (
        <StyledActivateOrForgotForm>
          {isActivateAccountFlow ? (
            <div className={clsx('row activate-form-container', { 'form-disabled': isLHSDisable && isSubmitted })}>
              <div className="col-12 px-2 px-md-1">{renderFields()}</div>
            </div>
          ) : (
            <div className="container column-container my-5 px-md-0">
              <div
                className={clsx(enablePhaseOne ? '' : 'col-12 offset-md-2 col-md-8 offset-lg-0 col-lg-5 px-0', {
                  'mx-auto': isEAA,
                })}
              >
                <Heading
                  element={Types.tags.h1}
                  variation={Types.headingType.title}
                  titleText={authorModel?.title}
                  customClass={`${errorsList?.length && !enablePhaseThree ? 'my-0' : 'mb-5'} ${
                    !enablePhaseThree ? 't-title-s' : 't-subtitle-xl'
                  }`}
                />
                {enablePhaseThree && (
                  <>
                    <hr className="flyout-divider" />
                    <div className="t-overline-alt-normal mt-4 mb-2">
                      {stepCounter?.replace('{0}', '1').replace('{1}', '2')}
                    </div>
                    <div className="t-subtitle-m mb-4">{authorModel?.subTitle}</div>
                  </>
                )}
                {!!errorsList?.length && (
                  <div>
                    <Messages messageType="error-sev1" className="mt-3 account-page-error-msg mb-4 px-2">
                      {errors?.prohibitedCountryErrMsg ? (
                        <div>
                          <Messages messageType="error-sev1" className="mt-3 account-page-error-msg mb-4 px-2">
                            <RichText text={authorModel?.prohibitedCountryErrMsg} componentId="error-span" />
                          </Messages>
                        </div>
                      ) : displayTypeOneErrorInside ? (
                        <div dangerouslySetInnerHTML={{ __html: errorsList[0] }}></div>
                      ) : (
                        <>
                          <span>{authorModel?.errorMessageForgotPwrd}</span>
                          {errorsList.map(error => (
                            <ul>
                              <li>{error}</li>
                            </ul>
                          ))}
                        </>
                      )}
                    </Messages>
                  </div>
                )}
                {renderFields()}
              </div>
            </div>
          )}
          {enablePhaseOne && <FlyOutModalStyle />}
        </StyledActivateOrForgotForm>
      )}
    </div>
  );
};
